type GetBuilderImageResizeUrlProps = {
  imageUrl?: string;
  width?: number;
  height?: number;
  format?: "webp" | "original";
};

/* This function is used to multiply the width and height by 2 for HiDPI (Retina) displays. */
function multiplyForHiDPI(value: number): number {
  return value * 2;
}

/**
 * https://www.builder.io/c/docs/image-api#image-api-explorer
 * Builder have image API that allows to resize images by adding query parameters to the image URL.
 * This function takes an image URL, width, height and format,
 * and returns a new URL with the appropriate query parameters for resizing the image.
 *
 * NOTE: At the time of writing this function,
 * the Builder image API has issues with quality parameter (lower quality makes payload bigger),
 * so we are only setting width and height for now.
 */
export function getBuilderImageResizeUrl(props: GetBuilderImageResizeUrlProps): string | undefined {
  const { imageUrl, width, height, format = "webp" } = props;

  if (!imageUrl) {
    return undefined;
  }

  let url: URL;

  try {
    // Check if the URL is valid
    url = new URL(imageUrl);
  } catch {
    return imageUrl;
  }

  // Ignore non-builder image URLs
  const isBuilderImageApiUrl = url.hostname === "cdn.builder.io" && url.pathname.startsWith("/api/v1/image");
  if (!isBuilderImageApiUrl) {
    return imageUrl;
  }

  // Width
  if (width && url.searchParams.get("width") === null) {
    url.searchParams.set("width", multiplyForHiDPI(width).toString());
  }

  // Height
  if (height && url.searchParams.get("height") === null) {
    url.searchParams.set("height", multiplyForHiDPI(height).toString());
  }

  // Format
  if (format && url.searchParams.get("format") === null) {
    url.searchParams.set("format", format);
  }

  return url.toString();
}
